import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, MARKS } from '@contentful/rich-text-types';
import { LuLink2, LuMinus, LuPlus } from 'react-icons/lu';
import { useTranslation } from '../context/TranslationContext';
import styles from '../styles/FAQs.module.css';
import { filterFaqGroupsByFlags, findMatches, localized, searchFaqGroups } from '../helpers/faqSearch';
import { useFeatureFlags } from '../context/FeatureFlagsContext';

export const richTextOptions = {
  renderMark: {
    [MARKS.BOLD]: text => <strong>{text}</strong>,
  },
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => (
      <p className={styles.paragraph}>{children}</p>
    ),
    [BLOCKS.UL_LIST]: (_, children) => (
      <ul className={styles.list}>{children}</ul>
    ),
    [BLOCKS.OL_LIST]: (_, children) => (
      <ol className={styles.list}>{children}</ol>
    ),
    [BLOCKS.LIST_ITEM]: (_, children) => (
      <li className={styles.listItem}>{children}</li>
    ),
  },
};

export const MOBILE_VIDEO_QUERY = '(max-width: 600px)';

export const faqElementId = anchor => `faq-${anchor}`;

export const faqLink = anchor =>
  `${window.location.origin}/help#${encodeURIComponent(anchor)}`;

const highlight = (text, query) => {
  const ranges = findMatches(text, query);
  if (!ranges.length) return text;
  const parts = [];
  let cursor = 0;
  ranges.forEach(([start, end]) => {
    if (start > cursor) parts.push(text.slice(cursor, start));
    parts.push(<mark key={start} className={styles.highlight}>{text.slice(start, end)}</mark>);
    cursor = end;
  });
  if (cursor < text.length) parts.push(text.slice(cursor));
  return parts;
};

const decodeHash = hash => {
  try {
    return decodeURIComponent(String(hash || '').replace(/^#/, ''));
  } catch {
    return '';
  }
};

const copyText = async text => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const area = document.createElement('textarea');
  area.value = text;
  area.setAttribute('readonly', '');
  area.style.position = 'fixed';
  area.style.opacity = '0';
  document.body.appendChild(area);
  area.select();
  document.execCommand('copy');
  area.remove();
};

const matchesMobile = () =>
  typeof window.matchMedia === 'function' && Boolean(window.matchMedia(MOBILE_VIDEO_QUERY)?.matches);

const useIsMobile = () => {
  const [mobile, setMobile] = useState(matchesMobile);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const query = window.matchMedia(MOBILE_VIDEO_QUERY);
    const onChange = event => setMobile(event.matches);
    query?.addEventListener?.('change', onChange);
    return () => query?.removeEventListener?.('change', onChange);
  }, []);
  return mobile;
};

const ToggleIcons = ({ size }) => (
  <>
    <LuPlus className={`${styles.icon} ${styles.iconPlus}`} size={size} />
    <LuMinus className={`${styles.icon} ${styles.iconMinus}`} size={size} />
  </>
);

export default function FAQs({ faqs: allFaqs = [] }) {
  const { t, language } = useTranslation();
  const { features, ready } = useFeatureFlags();
  // Flagged groups wait for the flags, so they never flash and deep links resolve once.
  const faqs = useMemo(
    () => (ready ? filterFaqGroupsByFlags(allFaqs, features) : []),
    [allFaqs, features, ready],
  );
  const { hash } = useLocation();
  const [query, setQuery] = useState('');
  const term = query.trim();
  const searchingRef = useRef(false);
  searchingRef.current = Boolean(term);

  const groups = useMemo(
    () => (term ? searchFaqGroups(faqs, term, language) : faqs),
    [faqs, term, language],
  );

  // Deep link (/help#anchor): open the question and its group, then bring it into view.
  useEffect(() => {
    const anchor = decodeHash(hash);
    if (!anchor || !faqs.length) return undefined;
    const target = document.getElementById(faqElementId(anchor));
    if (!target || target.tagName !== 'DETAILS') return undefined;
    target.open = true;
    const group = target.parentElement?.closest('details');
    if (group) group.open = true;
    const timer = setTimeout(() => {
      target.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    }, 150);
    return () => clearTimeout(timer);
  }, [hash, faqs]);

  // Opening a question puts its link in the address bar, so it can be copied and shared as is.
  const handleToggle = anchor => event => {
    if (!event.currentTarget.open || searchingRef.current) return;
    const { pathname, search } = window.location;
    window.history.replaceState(window.history.state, '', `${pathname}${search}#${encodeURIComponent(anchor)}`);
  };

  if (!faqs.length) return null;

  return (
    <div className={styles.faqs}>
      <h2 className={`heading mb-20 ${styles.title}`}>{t('faq.title')}</h2>

      <div className={styles.search} role="search">
        <input
          type="search"
          className={`portal-input ${styles.searchInput}`}
          placeholder={t('faqSearch.placeholder')}
          aria-label={t('faqSearch.placeholder')}
          value={query}
          onChange={event => setQuery(event.target.value)}
        />
        {query && (
          <button
            type="button"
            className={styles.clearSearch}
            aria-label={t('faqSearch.clear')}
            onClick={() => setQuery('')}
          >
            ✕
          </button>
        )}
      </div>

      {term && groups.length === 0 && (
        <p className={styles.noResults} role="status">
          {t('faqSearch.noResults').replace('{term}', term)}
        </p>
      )}

      <div>
        {groups.map((group, index) => (
          <FAQGroup
            key={group.id}
            group={group}
            term={term}
            defaultOpen={index === 0}
            onToggleItem={handleToggle}
          />
        ))}
      </div>
    </div>
  );
}

function FAQGroup({ group, term, defaultOpen, onToggleItem }) {
  const { language } = useTranslation();
  const faqs = (group?.faqs || []).filter(faq => faq && localized(faq.title, faq.titlePt, language));
  if (!faqs.length) return null;

  const title = localized(group.title, group.titlePt, language);

  return (
    <details
      className={styles.faqGroup}
      open={Boolean(term) || defaultOpen}
      aria-labelledby={`faq-group-${group.id}-title`}
    >
      <summary className={styles.faqGroupSummary}>
        <span id={`faq-group-${group.id}-title`} className={styles.faqGroupTitle}>
          {highlight(title || '', term)}
        </span>
        <span className={styles.faqIconWrap} aria-hidden="true">
          <ToggleIcons size={22} />
        </span>
      </summary>

      <div className={styles.faqList}>
          {faqs.map(faq => (
            <FAQItem key={faq.id} faq={faq} term={term} onToggle={onToggleItem(faq.anchor || faq.id)} />
          ))}
      </div>
    </details>
  );
}

function FAQItem({ faq, term, onToggle }) {
  const { language } = useTranslation();
  const anchor = faq.anchor || faq.id;
  const question = localized(faq.title, faq.titlePt, language);
  const answer = localized(faq.copy, faq.copyPt, language);
  const options = term
    ? { ...richTextOptions, renderText: text => highlight(text, term) }
    : richTextOptions;

  return (
    <details
      id={faqElementId(anchor)}
      className={styles.faqItem}
      open={Boolean(term)}
      onToggle={onToggle}
      aria-labelledby={`${faqElementId(anchor)}-title`}
    >
      <summary className={styles.faqSummary}>
        <span id={`${faqElementId(anchor)}-title`} className={styles.faqQuestion}>
          {highlight(question, term)}
        </span>
        <span className={styles.faqIconWrap} aria-hidden="true">
          <ToggleIcons size={18} />
        </span>
      </summary>

      <div className={styles.faqAnswer}>
        {answer && documentToReactComponents(answer, options)}
        <FAQVideo faq={faq} question={question} />
        <FAQCopyLink anchor={anchor} question={question} />
      </div>
    </details>
  );
}

// Desktop cut on wide screens, the vertical cut on phones; either one alone is used everywhere.
export function FAQVideo({ faq, question }) {
  const { t } = useTranslation();
  const mobile = useIsMobile();
  const source = (mobile && faq.videoMobile) || faq.video || faq.videoMobile;
  if (!source) return null;

  const caption = t('faq.videoCaption').replace('{question}', question || '');

  return (
    <figure className={styles.faqVideo}>
      <video
        key={source.src}
        className={styles.faqVideoPlayer}
        controls
        playsInline
        preload="metadata"
        poster={source.poster}
        aria-label={caption}
        src={source.src}
      />
      <figcaption className={styles.faqVideoCaption}>{caption}</figcaption>
    </figure>
  );
}

export function FAQCopyLink({ anchor, question }) {
  const { t } = useTranslation();
  const [status, setStatus] = useState('');

  useEffect(() => {
    if (!status) return undefined;
    const timer = setTimeout(() => setStatus(''), 2500);
    return () => clearTimeout(timer);
  }, [status]);

  const handleClick = async () => {
    const url = faqLink(anchor);
    try {
      await copyText(url);
      setStatus(t('faq.linkCopied'));
    } catch {
      setStatus(url);
    }
  };

  return (
    <div className={styles.faqLinkRow}>
      <button
        type="button"
        className={styles.faqLinkBtn}
        onClick={handleClick}
        aria-label={t('faq.copyLinkLabel').replace('{question}', question || '')}
      >
        <LuLink2 size={14} aria-hidden="true" />
        {t('faq.copyLink')}
      </button>
      {status && (
        <span className={styles.faqLinkStatus} role="status">
          {status}
        </span>
      )}
    </div>
  );
}
